import {
  ReactElement, useCallback, useEffect, useMemo, useState,
} from 'react';
import { BigNumbersContainer } from './styles';
import { Divider } from '../components/Divider';
import { useTranslation } from 'react-i18next';
import { useFilterContext } from '../contexts/FilterContext';
import { GetOverviewSummary } from '../types';
import { getOverviewSummary } from '../helpers/overview';
import { useToast } from 'contexts/newToastContext';
import { Units } from './Units';
import { useMediaQuery } from '~/hooks/useMediaQuery';
import { Healthy } from './Healthy';
import { UnitsDisponibility } from './UnitsDisponibility';
import { HvacEnergy } from './HvacEnergy';
import { ThermalComfort } from './ThermalComfort';

interface BigNumbersProps {
  hasHealth: boolean;
  hasThermalComfort: boolean;
  loading: boolean;
}

export function BigNumbers({
  hasHealth,
  hasThermalComfort,
  loading,
}: Readonly<BigNumbersProps>): ReactElement {
  const { selectedOptions, client, filtersDependencies } = useFilterContext();
  const { t } = useTranslation();
  const { toast } = useToast();

  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryData, setSummaryData] = useState<GetOverviewSummary | undefined>();

  const generalLoading = useMemo(() => loading || loadingSummary, [loading, loadingSummary]);

  const getSummaryInfo = useCallback(async () => {
    setLoadingSummary(true);

    const data = await getOverviewSummary(Number(client), {
      stateIds: selectedOptions.state,
      unitIds: selectedOptions.group?.units,
      healthStatus: selectedOptions.healthStatus,
      thermalComfortStatus: selectedOptions.status,
    });

    if (data) {
      setSummaryData(data);
    } else {
      toast(t('erroTitulo'), t('erroBuscaDadosContabilizacao'), 'error');
    }

    setLoadingSummary(false);
  }, [filtersDependencies]);

  useEffect(() => {
    if (client) {
      getSummaryInfo();
    }
  }, [filtersDependencies]);

  return (
    <BigNumbersContainer>
      <Units
        data={summaryData?.UNITS}
        loading={generalLoading}
      />
      <ResponsiveDivider />
      <Healthy
        hasHealth={hasHealth}
        loading={loading}
      />
      <ResponsiveDivider />
      <UnitsDisponibility
        data={summaryData?.DISPONIBILITY}
        loading={generalLoading}
      />
      <ResponsiveDivider />
      <ThermalComfort hasThermalComfort={hasThermalComfort} loading={generalLoading} />
      <ResponsiveDivider />
      <HvacEnergy loading={generalLoading} />
    </BigNumbersContainer>
  );
}

function ResponsiveDivider(): ReactElement {
  const isMobile = useMediaQuery('(max-width: 768px)');
  return (
    <Divider
      position={isMobile ? 'x' : 'y'}
      style={isMobile ? { width: '95%' } : { height: '104px' }}
    />
  );
}
